import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  PenLine,
  ShieldCheck,
} from "lucide-react";
import SignaturePadField from "../../../components/ui/signature-pad";
import {
  agruparPorBorda,
  painelBordeiroService,
  type AcaoCronometro,
  type ProducaoBordasHoje,
} from "../../../services/painelBordeiroService";
import CronometroBorda from "../../PainelBordeiro/components/CronometroBorda";
import UsoTamanho, {
  LIMITE_ESTOQUE,
  pctConsumo,
} from "../../PainelBordeiro/components/ConsumoEstoque";

type Props = { lojaId?: number };

const ProducaoBordasCards: React.FC<Props> = ({ lojaId }) => {
  const queryClient = useQueryClient();
  const queryKey = ["bordeiro-producao-bordas", lojaId ?? null];
  const [aberto, setAberto] = useState(true);
  const [finalizando, setFinalizando] = useState(false);
  const [responsavelNome, setResponsavelNome] = useState("");
  const [assinatura, setAssinatura] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey,
    queryFn: () => painelBordeiroService.getProducaoBordeiro(lojaId),
    refetchInterval: 20_000,
    staleTime: 0,
    retry: false,
  });

  const offsetMs = useMemo(
    () => (data ? new Date(data.serverNow).getTime() - Date.now() : 0),
    [data]
  );

  const aplicar = (resp: ProducaoBordasHoje) =>
    queryClient.setQueryData(queryKey, resp);

  const cronometro = useMutation({
    mutationFn: (v: { itemId: number; acao: AcaoCronometro }) =>
      painelBordeiroService.cronometro(v.itemId, v.acao),
    onSuccess: aplicar,
    onError: (e: Error) => {
      toast.error(e.message);
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const finalizar = useMutation({
    mutationFn: (producaoId: number) =>
      painelBordeiroService.finalizar(producaoId, {
        responsavelNome: responsavelNome.trim(),
        assinatura: assinatura!,
      }),
    onSuccess: (resp) => {
      aplicar(resp);
      setFinalizando(false);
      toast.success("Produção de bordas finalizada.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const producao = data?.producao;
  if (!producao || producao.status !== "em_producao") return null;

  const grupos = agruparPorBorda(
    producao.itens.filter((i) => i.quantidadePlanejada > 0 || i.consumido > 0)
  );
  const totalPlanejado = producao.itens.reduce(
    (s, i) => s + i.quantidadePlanejada,
    0
  );
  const totalSaiu = producao.itens.reduce((s, i) => s + i.consumido, 0);

  const confirmarFinalizacao = async () => {
    if (!responsavelNome.trim()) {
      toast.warn("Informe o nome do responsável.");
      return;
    }
    if (!assinatura) {
      toast.warn("Colete a assinatura do responsável.");
      return;
    }
    const r = await Swal.fire({
      title: "Finalizar produção de bordas?",
      text: "Cronômetros em andamento serão encerrados.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Finalizar e assinar",
      cancelButtonText: "Voltar",
      confirmButtonColor: "#16a34a",
    });
    if (r.isConfirmed) finalizar.mutate(producao.id);
  };

  return (
    <section className="border-b border-neutral-200 bg-neutral-50">
      <div className="flex items-center justify-between gap-3 px-4 py-2">
        <button
          type="button"
          onClick={() => setAberto((v) => !v)}
          className="flex items-center gap-2 text-sm font-semibold text-neutral-800"
        >
          <ClipboardCheck size={18} className="text-primary" />
          Produção do dia (estoque)
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
            {totalSaiu}/{totalPlanejado} saíram
          </span>
          {aberto ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {aberto && !finalizando && (
          <button
            type="button"
            onClick={() => setFinalizando(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90"
          >
            <PenLine size={14} /> Assinar e encerrar
          </button>
        )}
      </div>

      {aberto && (
        <div className="space-y-3 px-4 pb-3">
          <p className="text-xs text-neutral-600">
            Cada card é uma borda com a quantidade por tamanho. Pedidos abatem o
            estoque sozinhos; cards de pedido voltam a partir de{" "}
            {Math.round(LIMITE_ESTOQUE * 100)}% do planejado (por tamanho).
          </p>

          <div className="flex flex-wrap gap-2">
            {grupos.map((grupo) => {
              const planejado = grupo.tamanhos.reduce(
                (s, i) => s + i.quantidadePlanejada,
                0
              );
              // Liberação é por tamanho: só os tamanhos que passaram do limite.
              const tamanhosLiberados = grupo.tamanhos.filter(
                (i) =>
                  i.quantidadePlanejada > 0 &&
                  pctConsumo(i.consumido, i.quantidadePlanejada) >=
                    LIMITE_ESTOQUE
              );
              const liberouPedidos = tamanhosLiberados.length > 0;
              // Cronômetro é sincronizado no backend para todos os tamanhos da borda.
              const itemCronometro = grupo.tamanhos[0];

              return (
                <div
                  key={grupo.bordaChave}
                  className={`flex w-[280px] flex-col gap-2 rounded-lg border-2 bg-white p-3 shadow-sm ${
                    itemCronometro.cronometro.status === "rodando"
                      ? "border-emerald-400 ring-2 ring-emerald-100"
                      : liberouPedidos
                        ? "border-amber-400"
                        : "border-primary/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                        Borda
                      </p>
                      <h3 className="text-base font-bold text-neutral-900">
                        {grupo.bordaNome}
                      </h3>
                    </div>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
                      {planejado} un.
                    </span>
                  </div>

                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-neutral-100 text-[10px] uppercase text-neutral-500">
                        <th className="py-1 text-left font-semibold">
                          Tamanho
                        </th>
                        <th className="py-1 text-right font-semibold">Qtd</th>
                        <th className="py-1 text-right font-semibold">Uso</th>
                        <th className="py-1 text-right font-semibold">
                          Restam
                        </th>
                        <th className="py-1 text-right font-semibold">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-50">
                      {grupo.tamanhos.map((i) => (
                        <tr key={i.id}>
                          <td className="py-1.5 font-medium text-neutral-800">
                            {i.tamanho}
                          </td>
                          <td className="py-1.5 text-right font-semibold tabular-nums">
                            {i.quantidadePlanejada}
                          </td>
                          <td className="py-1.5 text-right tabular-nums text-neutral-600">
                            {i.consumido}
                          </td>
                          <td
                            className={`py-1.5 text-right font-semibold tabular-nums ${
                              i.excedente > 0
                                ? "text-red-600"
                                : "text-emerald-700"
                            }`}
                          >
                            {i.excedente > 0 ? i.excedente : i.restante}
                          </td>
                          <td className="py-1.5 pl-2">
                            <UsoTamanho
                              consumido={i.consumido}
                              planejado={i.quantidadePlanejada}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {liberouPedidos && (
                    <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] font-medium text-amber-800">
                      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                      {tamanhosLiberados
                        .map(
                          (i) =>
                            `${i.tamanho} em ${Math.round(
                              pctConsumo(i.consumido, i.quantidadePlanejada) *
                                100
                            )}%`
                        )
                        .join(", ")}{" "}
                      — cards de pedido liberados abaixo.
                    </div>
                  )}

                  <CronometroBorda
                    item={itemCronometro}
                    offsetMs={offsetMs}
                    disabled={cronometro.isPending}
                    onAcao={(acao) =>
                      cronometro.mutate({
                        itemId: itemCronometro.id,
                        acao,
                      })
                    }
                  />
                </div>
              );
            })}
          </div>

          {finalizando && (
            <div className="space-y-3 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-medium text-neutral-800">
                    Nome do responsável
                  </span>
                  <input
                    value={responsavelNome}
                    onChange={(e) => setResponsavelNome(e.target.value)}
                    maxLength={255}
                    className="rounded-lg border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </label>
                <SignaturePadField
                  label="Assinatura"
                  onSignedChange={(ok, url) =>
                    setAssinatura(ok ? (url ?? null) : null)
                  }
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setFinalizando(false)}
                  disabled={finalizar.isPending}
                  className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => void confirmarFinalizacao()}
                  disabled={finalizar.isPending}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  <ShieldCheck size={16} /> Finalizar e assinar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ProducaoBordasCards;
